/**
 * PRD-28 §FR-3 — tiny context that exposes ``openFeedback()`` to any
 * call site. The Sidebar entry uses it; future surfaces (e.g. an
 * error-boundary "Report this" link) can reuse the same modal
 * without re-mounting it per call site.
 *
 * One <FeedbackProvider> mounts in App.tsx wrapping the rest of the
 * authenticated tree. The modal itself renders only when open, so
 * unmounted users pay no cost.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react"

import { FeedbackModal } from "./components/FeedbackModal"

interface FeedbackContextValue {
  openFeedback: () => void
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const openFeedback = useCallback(() => setOpen(true), [])
  const closeFeedback = useCallback(() => setOpen(false), [])

  const value = useMemo(() => ({ openFeedback }), [openFeedback])

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      {open ? <FeedbackModal onClose={closeFeedback} /> : null}
    </FeedbackContext.Provider>
  )
}

export function useFeedback(): FeedbackContextValue {
  const ctx = useContext(FeedbackContext)
  if (!ctx) {
    // Falling back to a no-op keeps the Sidebar testable / renderable
    // outside the App tree. Real misuse surfaces in dev via the
    // console.warn below.
    // Misuse falls back to a no-op so the Sidebar stays renderable
    // outside the App tree (e.g. Storybook); a real consumer that
    // misuses this will see its `openFeedback` button do nothing,
    // which is loud enough at the call-site to debug.
    return { openFeedback: () => {} }
  }
  return ctx
}
